import React from 'react'
import PropTypes from 'prop-types'
import { deepMerge } from 'grommet/utils'
import { ThemeProvider } from 'styled-components'
import { HeaderTwo } from 'secondstep-components'
import { toUrlSafe } from 'utils/stringHelpers'
import SiteSelector from 'components/SiteSelector'
import ProgramRoleTile from 'components/ProgramRoleTile'
import K8ProgramRoleTile from 'components/K8ProgramRoleTile'

import StaticLink from 'components/dashboard/ActiveTabProgramCards/StaticLink'

import {
  Background,
  Body,
  Content,
  HeaderBox,
  HighSchoolProgramHeader,
  TileContainer,
  SiteSelectorWrapperForK8,
} from './component.styles'
import themeMap from './data'
import ProgramTitle from './ProgramTitle'
import { superscriptRegistered } from './helpers'

const ProgramCard = React.forwardRef(
  ({
    children,
    currentSite,
    displayTitle,
    displayTitleLink,
    isHighSchool,
    isHighSchoolFamily,
    isHsInstancedView,
    isK5Product,
    isMindYetiProduct,
    isMsProduct,
    isOstProduct,
    isSelaProduct,
    onComponentClick,
    program,
    theme,
    areTeachersPreferencesSet,
    programPreferences,
    instructorData,
  }) => {
    const { highSchoolEducatorPreferences, isSetup } = currentSite || {}
    const { hasUpdatedProfile } = highSchoolEducatorPreferences || {}
    const programTheme = deepMerge(theme, themeMap[program])
    const headerTestId = `header-${toUrlSafe(displayTitle)}`

    // TODO For now we are hardcoding this static link until we decide how to populate them in this navbar
    const exploreLessonsLink = {
      internalTitle: 'elem browse lessons',
      displayTitle: 'Browse Lessons and Materials',
      linkLabel: 'Explore Lessons',
      path: isK5Product
        ? '/product/elementary/lessons'
        : '/product/middle-school/lessons',
    }

    return (
      <ThemeProvider theme={programTheme}>
        <Background
          dataTestId={`program-card-${toUrlSafe(program)}`}
          isHighSchoolFamily={isHighSchoolFamily}
          isMindYetiProduct={isMindYetiProduct}
          isOstProduct={isOstProduct}
          isSelaProduct={isSelaProduct}
          program={program}
        >
          <Body isSelaProduct={isSelaProduct}>
            <HeaderBox
              isHsInstancedView={isHsInstancedView}
              isK5Product={isK5Product}
              isMsProduct={isMsProduct}
              isSelaProduct={isSelaProduct}
            >
              {isHighSchool && (
                <>
                  <HighSchoolProgramHeader key="header-welcome-displaytitle">
                    {superscriptRegistered(displayTitle)}
                  </HighSchoolProgramHeader>
                  <TileContainer>
                    <SiteSelector />
                    {isSetup && hasUpdatedProfile && <ProgramRoleTile />}
                  </TileContainer>
                </>
              )}
              {!isHighSchool && (
                <>
                  <div className="title-container">
                    <HeaderTwo
                      dataTestId={headerTestId}
                      key="header-welcome-displaytitle"
                    >
                      <ProgramTitle
                        displayTitle={displayTitle}
                        displayTitleLink={displayTitleLink}
                        isHighSchool={isHighSchool}
                        isSelaProduct={isSelaProduct}
                        onComponentClick={onComponentClick}
                      />
                    </HeaderTwo>
                    <div className="title-button-block">
                      {(isMsProduct || isK5Product) && (
                        <div className="site-selector">
                          <SiteSelectorWrapperForK8>
                            <SiteSelector />
                          </SiteSelectorWrapperForK8>
                        </div>
                      )}

                      {(isK5Product || isMsProduct) &&
                        areTeachersPreferencesSet && (
                          <K8ProgramRoleTile
                            instructorData={instructorData}
                            isK5Product={isK5Product}
                            isMsProduct={isMsProduct}
                            programPreferences={programPreferences}
                          />
                        )}

                      {(isK5Product || isMsProduct) && (
                        <StaticLink
                          onComponentClick={onComponentClick}
                          staticLink={exploreLessonsLink}
                        />
                      )}
                    </div>
                  </div>
                </>
              )}
            </HeaderBox>
            <Content isHighSchool={isHighSchool}>{children}</Content>
          </Body>
        </Background>
      </ThemeProvider>
    )
  },
)

ProgramCard.propTypes = {
  areTeachersPreferencesSet: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  currentSite: PropTypes.object,
  displayTitle: PropTypes.string,
  displayTitleLink: PropTypes.string,
  firstTime: PropTypes.bool,
  instructorData: PropTypes.object,
  isHighSchool: PropTypes.bool,
  isHighSchoolFamily: PropTypes.bool,
  isHsInstancedView: PropTypes.bool,
  isK5Product: PropTypes.bool,
  isMindYetiProduct: PropTypes.bool,
  isMsProduct: PropTypes.bool,
  isOstProduct: PropTypes.bool,
  isSelaProduct: PropTypes.bool,
  onComponentClick: PropTypes.func.isRequired,
  program: PropTypes.string,
  programPreferences: PropTypes.object,
  purposeIsInDraft: PropTypes.bool,
  theme: PropTypes.object,
  userContext: PropTypes.object,
}

export default ProgramCard
